import { Game } from "@aura-frontend/omnibus-az-client";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import {
  cn,
  DateRange,
  DateRangePicker,
  Icons,
  Input,
  Label,
  Select,
  Spinner,
  Table,
} from "@win-reality/react-ui";
import { FileCheck, FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const EVENT_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "game", label: "Game" },
  { value: "bullpen", label: "Bullpen" },
  { value: "batting_practice", label: "Batting Practice" },
];

type EventRow = {
  id: string;
  name: string;
  date: Date;
  eventType: string;
  rawData: Game;
};

type EventsTableProps = {
  games: Game[];
  isLoading: boolean;
  onEdit?: (game: Game) => void;
  onDownload?: (game: Game) => void;
  onGenerateReport?: (game: Game) => void;
  downloadingGameId?: string | null;
  generatingReportGameIds?: Set<string>;
  selectedEventType: string;
  onEventTypeChange: (type: string) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  generatedReportGameIds?: Set<string>;
};

export const EventsTable = ({
  games,
  isLoading,
  onEdit,
  onDownload,
  onGenerateReport,
  downloadingGameId,
  generatingReportGameIds = new Set(),
  selectedEventType,
  onEventTypeChange,
  dateRange,
  onDateRangeChange,
  generatedReportGameIds = new Set(),
}: EventsTableProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortingState, setSortingState] = useState<SortingState>([]);

  const itemsPerPage = 15;

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedEventType, dateRange, searchQuery]);

  const rows: EventRow[] = useMemo(() => {
    return (games ?? []).map((game) => {
      const gameUuid = game.gameUuid ?? "";
      const displayName = game.displayName ?? "Game";
      const startTime = game.times?.[0]?.startTime ?? 0;
      const gameType = game.type ?? "game";

      return {
        id: gameUuid,
        name: displayName,
        date: new Date(Number(startTime) * 1000),
        eventType: gameType,
        rawData: game,
      };
    });
  }, [games]);

  // Client-side search filtering
  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;

    return rows.filter((row) =>
      row.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [rows, searchQuery]);

  const getBadgeColors = (type: string) => {
    switch (type) {
      case "game":
        return "bg-badge-batter/20 text-text-light";
      case "bullpen":
        return "bg-badge-pitcher/20 text-text-light";
      case "batting_practice":
        return "bg-badge-catcher/20 text-text-light";
      default:
        return "bg-slate-100 text-slate-500";
    }
  };

  const columns: ColumnDef<EventRow>[] = useMemo(() => {
    const baseColumns: ColumnDef<EventRow>[] = [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => <span>{row.original.name}</span>,
      },
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }) => {
          const date = row.original.date;
          const isValidDate = !isNaN(date.getTime());
          if (!isValidDate) return <span>-</span>;
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const year = date.getFullYear();
          const timeString = date
            .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            .replace(/^0+/, "");
          return (
            <span>
              {month}/{day}/{year} · {timeString}
            </span>
          );
        },
      },
      {
        accessorKey: "eventType",
        header: "Type",
        cell: ({ row }) => {
          const type = row.original.eventType;
          const typeLabel =
            EVENT_TYPE_OPTIONS.find((opt) => opt.value === type)?.label ?? type;
          const colors = getBadgeColors(type);
          return (
            <span
              className={cn(
                "inline-flex items-center px-3.5 py-2 rounded-full text-sm font-normal",
                colors,
              )}
            >
              {typeLabel}
            </span>
          );
        },
      },
    ];

    // Add Actions column if either action is provided
    if (onEdit || onDownload || onGenerateReport) {
      baseColumns.push({
        id: "actions",
        header: "",
        size: 150,
        enableSorting: false,
        cell: ({ row }) => {
          const gameId = row.original.rawData.gameUuid;
          const isDownloadingThis = downloadingGameId === gameId;
          const isGeneratingThisReport =
            !!gameId && generatingReportGameIds.has(gameId);
          const hasGeneratedReport =
            !!gameId && generatedReportGameIds.has(gameId);

          return (
            <div className="flex items-center justify-end gap-6">
              {onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(row.original.rawData)}
                  aria-label="Edit event"
                  title="Edit Event"
                  className="text-blue hover:text-blue/70"
                >
                  <Icons.PencilOutline className="h-5 w-5" />
                </button>
              )}
              {onDownload && (
                <button
                  type="button"
                  onClick={() => onDownload(row.original.rawData)}
                  aria-label="Download CSV"
                  disabled={isDownloadingThis}
                  title="Download CSV"
                  className="text-blue hover:text-blue/70 disabled:opacity-50"
                >
                  {isDownloadingThis ? (
                    <Spinner className="h-5 w-5" />
                  ) : (
                    <Icons.CloudDownloadOutline className="h-5 w-5" />
                  )}
                </button>
              )}
              {onGenerateReport && (
                <button
                  type="button"
                  onClick={() => onGenerateReport(row.original.rawData)}
                  disabled={hasGeneratedReport || isGeneratingThisReport}
                  aria-label="Generate Report"
                  title={
                    hasGeneratedReport ? "Report Generated" : "Generate Report"
                  }
                  className={cn(
                    "transition-colors",
                    hasGeneratedReport
                      ? "text-green-600 cursor-not-allowed"
                      : "text-blue hover:text-blue/70 disabled:opacity-50",
                  )}
                >
                  {isGeneratingThisReport ? (
                    <Spinner className="h-5 w-5" />
                  ) : hasGeneratedReport ? (
                    <FileCheck className="h-5 w-5" strokeWidth={1.5} />
                  ) : (
                    <FileText className="h-5 w-5" strokeWidth={1.5} />
                  )}
                </button>
              )}
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [
    onEdit,
    onDownload,
    onGenerateReport,
    downloadingGameId,
    generatingReportGameIds,
    generatedReportGameIds,
  ]);

  const handlePaginationChange = (
    updater:
      | { pageSize: number; pageIndex: number }
      | ((old: { pageSize: number; pageIndex: number }) => {
          pageSize: number;
          pageIndex: number;
        }),
  ) => {
    const newState =
      typeof updater === "function"
        ? updater({ pageSize: itemsPerPage, pageIndex: currentPage })
        : updater;
    setCurrentPage(newState.pageIndex);
  };

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex flex-col gap-2 ">
          <Label className="text-sm mb-0">Search</Label>
          <Input
            type="text"
            placeholder="Search by event name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            isClearable
            onClear={() => setSearchQuery("")}
            iconRight={
              <Icons.MagnifyingGlass className="h-5 w-5 text-placeholder" />
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm mb-0">Type</Label>
          <Select
            items={EVENT_TYPE_OPTIONS}
            value={selectedEventType}
            onValueChange={onEventTypeChange}
            placeholder="All"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm mb-0">Event Date Range</Label>
          <DateRangePicker
            range={dateRange}
            onRangeChange={onDateRangeChange}
          />
        </div>
      </div>

      <div className="mt-8">
        <Table
          variant="pagination"
          data={filteredRows}
          columns={columns}
          total={filteredRows.length}
          sortingState={sortingState}
          onSortingChange={setSortingState}
          paginationState={{ pageSize: itemsPerPage, pageIndex: currentPage }}
          onPaginationChange={handlePaginationChange}
          isFetching={isLoading}
          rowClassName={"shadow-glow-tiny rounded"}
          rowsWrapperClassName="gap-y-4"
          tableWrapperClassName="overflow-auto py-1"
          paginationClassName=""
        />
      </div>
    </div>
  );
};
